import { api, ApiError } from '../api.js'
import { isHistoryInstant } from '../dateTime/historyDateTime.js'

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const pageSizes = new Set([25, 50, 100])
const facets = new Set(['SHIFT', 'TAG', 'PUMP', 'NOZZLE', 'TANK', 'FUEL'])
const shiftModes = new Set(['ALL', 'NUMBER', 'UNASSIGNED'])
const tagModes = new Set(['ALL', 'REF', 'ABSENT', 'UNAVAILABLE'])
const maximumRangeMilliseconds = 366 * 24 * 60 * 60 * 1000
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const invalid = (kind = 'response') => new ApiError(`Invalid PTS-2 transaction ${kind}`, kind === 'query' ? 400 : 502,
  `pts2_transaction_${kind}_invalid`)

function positive(value, maximum = Number.MAX_SAFE_INTEGER) {
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 1 || number > maximum) throw invalid('query')
  return number
}

function optionalPositive(value, maximum) {
  return value == null || value === '' ? null : positive(value, maximum)
}

function positiveSet(value, maximumItems, maximumValue) {
  if (value == null) return []
  if (!Array.isArray(value) || value.length > maximumItems) throw invalid('query')
  const result = value.map((item) => positive(item, maximumValue))
  if (new Set(result).size !== result.length) throw invalid('query')
  return result
}

function pluralWithScalarAlias(value, pluralField, scalarField, maximumItems, maximumValue) {
  const pluralPresent = value[pluralField] != null
  const scalarPresent = value[scalarField] != null && value[scalarField] !== ''
  if (pluralPresent && scalarPresent) throw invalid('query')
  return pluralPresent ? positiveSet(value[pluralField], maximumItems, maximumValue)
    : scalarPresent ? [positive(value[scalarField], maximumValue)] : []
}

function iso(value, exclusiveEnd = false) {
  const date = new Date(value)
  if (!isHistoryInstant(value, { exclusiveEnd })) throw invalid('query')
  return date.toISOString()
}

export function transactionRangeIssue(from, to) {
  const start = Date.parse(from), end = Date.parse(to)
  if (!isHistoryInstant(from) || !isHistoryInstant(to, { exclusiveEnd: true })) return 'REQUIRED'
  if (start >= end) return 'ORDER'
  if (end - start > maximumRangeMilliseconds) return 'TOO_LONG'
  return null
}

export function normalizeTransactionFilters(value = {}) {
  const result = {
    stationId: positive(value.stationId),
    controllerId: positive(value.controllerId),
    from: iso(value.from),
    to: iso(value.to, true),
    transactionNumber: optionalPositive(value.transactionNumber, 65535),
    pumpNumbers: pluralWithScalarAlias(value, 'pumpNumbers', 'pumpNumber', 100, 100),
    nozzleNumbers: positiveSet(value.nozzleNumbers, 6, 6),
    tankNumbers: pluralWithScalarAlias(value, 'tankNumbers', 'tankNumber', 20, 20),
    fuelGradeIds: positiveSet(value.fuelGradeIds, 20, 20),
    shiftMode: value.shiftMode || 'ALL',
    shiftNumber: optionalPositive(value.shiftNumber, 999999999),
    tagMode: value.tagMode || 'ALL',
    tagRef: value.tagRef || null,
  }
  if (!shiftModes.has(result.shiftMode) || (result.shiftMode === 'NUMBER') !== (result.shiftNumber !== null)) throw invalid('query')
  if (!tagModes.has(result.tagMode) || (result.tagMode === 'REF') !== Boolean(result.tagRef)
    || result.tagRef && !uuid.test(result.tagRef)) throw invalid('query')
  if (transactionRangeIssue(result.from, result.to)) throw invalid('query')
  return result
}

export function filterPayload(value) {
  return Object.fromEntries(Object.entries(normalizeTransactionFilters(value)).filter(([, item]) => item !== null
    && !(Array.isArray(item) && !item.length) && item !== 'ALL'))
}

function queryFor(filters, page, limit) {
  page = positive(page, 10000)
  limit = Number(limit)
  if (!pageSizes.has(limit)) throw invalid('query')
  const payload = filterPayload(filters)
  const scalarFilters = Object.fromEntries(Object.entries(payload).filter(([, value]) => !Array.isArray(value)))
  const query = new URLSearchParams({ ...scalarFilters, page: String(page), limit: String(limit) })
  for (const field of ['pumpNumbers', 'nozzleNumbers', 'tankNumbers', 'fuelGradeIds']) {
    if (payload[field]?.length) query.set(field, payload[field].join(','))
  }
  return query
}

function row(value, stationId, controllerId) {
  if (!isRecord(value) || !uuid.test(value.id) || value.stationId !== stationId || value.controllerId !== controllerId
    || !(value.transactionNumber === null || Number.isSafeInteger(value.transactionNumber))) throw invalid()
  return structuredClone(value)
}

function transactionPage(value, filters, requestedLimit) {
  if (!isRecord(value) || !Array.isArray(value.items) || value.items.length > requestedLimit
    || !Number.isSafeInteger(value.page) || value.page < 1 || value.limit !== requestedLimit
    || !Number.isSafeInteger(value.totalItems) || value.totalItems < 0
    || !Number.isSafeInteger(value.totalPages) || value.totalPages < 0) throw invalid()
  return { ...value, items: value.items.map((item) => row(item, filters.stationId, filters.controllerId)) }
}

export async function getPts2Transactions(filters, { page = 1, limit = 25, signal } = {}) {
  filters = normalizeTransactionFilters(filters)
  return transactionPage(await api(`/pts2/transactions/browse?${queryFor(filters, page, limit)}`, { signal }), filters, limit)
}

export async function getPts2TransactionDetail(stationId, controllerId, transactionId, { signal } = {}) {
  stationId = positive(stationId)
  controllerId = positive(controllerId)
  if (!uuid.test(transactionId)) throw invalid('query')
  const value = await api(`/pts2/transactions/${transactionId}?stationId=${stationId}&controllerId=${controllerId}`, { signal })
  if (!isRecord(value) || !isRecord(value.transaction) || !isRecord(value.tag) || !isRecord(value.user)) throw invalid()
  value.transaction = row(value.transaction, stationId, controllerId)
  return structuredClone(value)
}

export async function getPts2TransactionFilterOptions(filters, facet, { search = '', cursor = null, limit = 100, signal } = {}) {
  filters = normalizeTransactionFilters(filters)
  if (!facets.has(facet) || !Number.isSafeInteger(limit) || limit < 1 || limit > 100
    || typeof search !== 'string' || search.length > 80
    || !(cursor === null || typeof cursor === 'string' && cursor.length > 0 && cursor.length <= 512)) throw invalid('query')
  const value = await api('/pts2/transactions/filter-options', {
    method: 'POST', signal,
    body: JSON.stringify({ filters: filterPayload(filters), facet, ...(search ? { search } : {}),
      ...(cursor ? { cursor } : {}), limit }),
  })
  if (!isRecord(value) || value.facet !== facet || !Array.isArray(value.items) || value.items.length > limit
    || typeof value.hasMore !== 'boolean'
    || !(value.nextCursor === null || typeof value.nextCursor === 'string' && value.nextCursor.length > 0 && value.nextCursor.length <= 512)
    || value.hasMore !== Boolean(value.nextCursor) || value.nextCursor !== null && (value.items.length === 0 || value.nextCursor === cursor)) throw invalid()
  return structuredClone(value)
}

export function paginationItems(current, total) {
  if (total <= 1) return total ? [1] : []
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1)
  const values = [...new Set([1, total, current - 1, current, current + 1])]
    .filter((value) => value >= 1 && value <= total).sort((left, right) => left - right)
  return values.flatMap((value, index) => index && value - values[index - 1] > 1 ? [`ellipsis-${value}`, value] : [value])
}
